import { useEffect, useMemo, useState, type ReactElement, type FormEvent } from 'react';
import { Heart, Moon, Activity } from 'lucide-react';
import {
  useReadiness,
  useTodayBiometrics,
  useUpsertBiometrics,
  readinessBorderClass,
  readinessLabel,
  type BiometricEntry,
  type ReadinessAvailable,
} from '@/lib/biometrics';
import { MetricTooltip } from '@/components/MetricTooltip';
import type { MetricKey } from '@/lib/metrics-glossary';

/**
 * Card matutino (M2.32.A) — entrada rápida de HRV/RHR/sueño + readiness score.
 *
 * Decisiones:
 *  - Se monta encima del HeroStatusBar en `Dashboard.tsx` para que sea lo primero
 *    que el atleta vea al abrir la app por la mañana.
 *  - Si NO hay entrada de hoy → form compacto con 3 inputs (RHR / HRV / sueño).
 *  - Si SÍ hay entrada → resumen "HR 52 · HRV 58 · 7.5h · Readiness 78/100 (verde)"
 *    + botón "Editar" que vuelve al form pre-rellenado.
 *  - Si no hay readiness aún (sin baseline o sin entrada) → muestra "Necesita
 *    más datos" sin romper.
 *  - Borde por color del nivel de readiness (green-300 / amber-300 / rose-300).
 *  - Inputs HTML5 number con steps razonables. No usamos Radix Form — sencillo,
 *    sin dependencias extras.
 */
export function MorningBiometricsCard(): ReactElement | null {
  const todayQuery = useTodayBiometrics();
  const readinessQuery = useReadiness();
  const upsertMutation = useUpsertBiometrics();

  // No renderizamos NADA mientras carga la primera vez para evitar layout shift.
  if (todayQuery.isLoading) return null;
  if (todayQuery.isError) {
    // No es crítico — fallamos en silencio (la card desaparece). Otros bloques
    // del dashboard siguen funcionando.
    return null;
  }
  const data = todayQuery.data;
  if (!data) return null;

  const readiness = readinessQuery.data?.available
    ? readinessQuery.data
    : null;

  return (
    <MorningBiometricsCardBody
      dateISO={data.dateISO}
      todayEntry={data.item}
      readiness={readiness}
      upsertPending={upsertMutation.isPending}
      onSubmit={(body) =>
        upsertMutation.mutate(
          { dateISO: data.dateISO, body },
          { onSuccess: () => undefined },
        )
      }
    />
  );
}

interface MorningBiometricsCardBodyProps {
  dateISO: string;
  todayEntry: BiometricEntry | null;
  readiness: ReadinessAvailable | null;
  upsertPending: boolean;
  onSubmit: (body: {
    rhrBpm?: number | null;
    hrvRmssdMs?: number | null;
    sleepHours?: number | null;
  }) => void;
}

export function MorningBiometricsCardBody({
  dateISO,
  todayEntry,
  readiness,
  upsertPending,
  onSubmit,
}: MorningBiometricsCardBodyProps): ReactElement {
  const hasEntry = todayEntry !== null;
  // Modo: 'view' cuando hay entrada y no estamos editando; 'edit' siempre que
  // no haya entrada o el usuario pulse "Editar".
  const [editing, setEditing] = useState<boolean>(!hasEntry);
  // Sincroniza el modo si llega entrada después de un upsert exitoso.
  useEffect(() => {
    if (hasEntry) setEditing(false);
  }, [hasEntry]);

  const borderClass = readiness
    ? readinessBorderClass(readiness.level)
    : 'border-slate-200';

  return (
    <article
      data-testid="morning-biometrics-card"
      aria-label="Biometría matutina"
      className={`rounded-xl border-2 ${borderClass} bg-white p-4 shadow-sm`}
    >
      <header className="mb-3 flex items-baseline justify-between gap-3">
        <h2 className="text-sm font-semibold text-slate-900">
          Biometría matutina
        </h2>
        <p className="text-xs text-slate-500">{formatDate(dateISO)}</p>
      </header>

      {editing ? (
        hasEntry ? (
          <BiometricsForm
            initial={todayEntry}
            pending={upsertPending}
            onSubmit={onSubmit}
            onCancel={() => setEditing(false)}
          />
        ) : (
          <BiometricsForm
            initial={todayEntry}
            pending={upsertPending}
            onSubmit={onSubmit}
          />
        )
      ) : (
        <BiometricsSummary
          entry={todayEntry!}
          readiness={readiness}
          onEdit={() => setEditing(true)}
        />
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------
// Form (entrada / edición)
// ---------------------------------------------------------------------------

function BiometricsForm({
  initial,
  pending,
  onSubmit,
  onCancel,
}: {
  initial: BiometricEntry | null;
  pending: boolean;
  onSubmit: (body: {
    rhrBpm?: number | null;
    hrvRmssdMs?: number | null;
    sleepHours?: number | null;
  }) => void;
  onCancel?: () => void;
}): ReactElement {
  const [rhr, setRhr] = useState<string>(
    initial?.rhrBpm !== null && initial?.rhrBpm !== undefined
      ? String(initial.rhrBpm)
      : '',
  );
  const [hrv, setHrv] = useState<string>(
    initial?.hrvRmssdMs !== null && initial?.hrvRmssdMs !== undefined
      ? String(initial.hrvRmssdMs)
      : '',
  );
  const [sleep, setSleep] = useState<string>(
    initial?.sleepHours !== null && initial?.sleepHours !== undefined
      ? String(initial.sleepHours)
      : '',
  );

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const body: {
      rhrBpm?: number | null;
      hrvRmssdMs?: number | null;
      sleepHours?: number | null;
    } = {};
    if (rhr.trim() !== '') {
      const n = Number(rhr);
      if (Number.isFinite(n)) body.rhrBpm = Math.round(n);
    } else if (initial?.rhrBpm !== null && initial?.rhrBpm !== undefined) {
      body.rhrBpm = null;
    }
    if (hrv.trim() !== '') {
      const n = Number(hrv);
      if (Number.isFinite(n)) body.hrvRmssdMs = Math.round(n);
    } else if (
      initial?.hrvRmssdMs !== null &&
      initial?.hrvRmssdMs !== undefined
    ) {
      body.hrvRmssdMs = null;
    }
    if (sleep.trim() !== '') {
      const n = Number(sleep);
      if (Number.isFinite(n)) body.sleepHours = n;
    } else if (
      initial?.sleepHours !== null &&
      initial?.sleepHours !== undefined
    ) {
      body.sleepHours = null;
    }
    onSubmit(body);
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_1fr_auto] sm:items-end"
    >
      <NumericField
        id="biometrics-rhr"
        label="RHR (bpm)"
        icon={<Heart className="h-3.5 w-3.5" aria-hidden="true" />}
        value={rhr}
        onChange={setRhr}
        min={30}
        max={120}
        step={1}
        placeholder="52"
        metric="rhr"
      />
      <NumericField
        id="biometrics-hrv"
        label="HRV (ms)"
        icon={<Activity className="h-3.5 w-3.5" aria-hidden="true" />}
        value={hrv}
        onChange={setHrv}
        min={5}
        max={200}
        step={1}
        placeholder="58"
        metric="hrv"
      />
      <NumericField
        id="biometrics-sleep"
        label="Sueño (h)"
        icon={<Moon className="h-3.5 w-3.5" aria-hidden="true" />}
        value={sleep}
        onChange={setSleep}
        min={0}
        max={14}
        step={0.25}
        placeholder="7.5"
        metric="sleep"
      />
      <div className="flex gap-2 sm:flex-col">
        <button
          type="submit"
          disabled={pending}
          aria-busy={pending}
          data-testid="biometrics-save"
          className="inline-flex items-center justify-center rounded-md bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {pending ? 'Guardando…' : 'Guardar'}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center justify-center rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-teal-600"
          >
            Cancelar
          </button>
        )}
      </div>
    </form>
  );
}

function NumericField({
  id,
  label,
  icon,
  value,
  onChange,
  min,
  max,
  step,
  placeholder,
  metric,
}: {
  id: string;
  label: string;
  icon: ReactElement;
  value: string;
  onChange: (v: string) => void;
  min: number;
  max: number;
  step: number;
  placeholder: string;
  /** Clave del glosario M2.32.E — añade icono "?" junto al label. */
  metric?: MetricKey;
}): ReactElement {
  return (
    <div className="flex flex-col gap-1">
      <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {icon}
        {/* El <label> envuelve SOLO el texto del label, no el botón "?", para
            que `getByLabelText(...)` siga resolviendo únicamente al input. */}
        <label htmlFor={id}>{label}</label>
        {metric && <MetricTooltip metric={metric} iconOnly />}
      </span>
      <input
        id={id}
        type="number"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        min={min}
        max={max}
        step={step}
        placeholder={placeholder}
        className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm tabular-nums focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Resumen (cuando hay entrada y no estamos editando)
// ---------------------------------------------------------------------------

function BiometricsSummary({
  entry,
  readiness,
  onEdit,
}: {
  entry: BiometricEntry;
  readiness: ReadinessAvailable | null;
  onEdit: () => void;
}): ReactElement {
  const parts: string[] = [];
  if (entry.rhrBpm !== null) parts.push(`HR ${entry.rhrBpm}`);
  if (entry.hrvRmssdMs !== null) parts.push(`HRV ${entry.hrvRmssdMs}`);
  if (entry.sleepHours !== null) parts.push(`${entry.sleepHours}h`);

  const readinessText = readiness
    ? `Readiness ${readiness.score}/100 (${readinessLabel(readiness.level)})`
    : 'Readiness: necesita más datos';

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p
        data-testid="biometrics-summary"
        className="text-sm text-slate-800"
      >
        <span aria-hidden="true">✓ </span>
        <span className="font-medium">{parts.join(' · ') || 'Sin datos'}</span>
        <span className="mx-2 text-slate-300">·</span>
        <span
          className={readiness ? `font-semibold ${readinessBadgeText(readiness.level)}` : 'text-slate-500'}
          data-testid="biometrics-readiness-text"
        >
          <MetricTooltip metric="readiness">{readinessText}</MetricTooltip>
        </span>
      </p>
      <button
        type="button"
        onClick={onEdit}
        data-testid="biometrics-edit"
        className="inline-flex items-center rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-teal-600"
      >
        Editar
      </button>
    </div>
  );
}

function readinessBadgeText(level: 'green' | 'yellow' | 'red'): string {
  switch (level) {
    case 'green':
      return 'text-emerald-700';
    case 'yellow':
      return 'text-amber-700';
    case 'red':
      return 'text-rose-700';
  }
}

// ---------------------------------------------------------------------------
// Helpers de presentación
// ---------------------------------------------------------------------------

function formatDate(dateISO: string): string {
  try {
    const d = new Date(`${dateISO}T00:00:00`);
    return new Intl.DateTimeFormat('es-ES', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
    }).format(d);
  } catch {
    return dateISO;
  }
}

// Marker para evitar advertencia de "useMemo no usado" en builds futuros si se
// reorganiza el archivo.
void useMemo;
